import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Flex, Text, HStack, Button, IconButton, Icon, useToast,
  Menu, MenuButton, MenuList, MenuItem, MenuDivider,
} from '@chakra-ui/react';
import {
  FiGrid, FiList, FiUser, FiArrowLeft, FiMaximize2, FiDownload, FiUsers, FiHardDrive, FiSearch, FiChevronDown, FiActivity, FiUserMinus,
} from 'react-icons/fi';
import ScannerAdmin from '../ScannerAdmin';
import AccessLogs from '../AccessLogs';
import StudentStatusesTab from '../StudentStatusAdmin/components/StudentStatusesTab';
import PedagogicalExitsTab from '../StudentStatusAdmin/components/PedagogicalExitsTab';
import SickExitsTab from '../StudentStatusAdmin/components/SickExitsTab';
import PickupExitsTab from '../StudentStatusAdmin/components/PickupExitsTab';
import AttendanceView from './components/AttendanceView';
import { getAccessLogs } from 'services/qrService';
import { useRoleModuleAccess, useCanOpenScanner, MODULES } from 'utils/site';

// Botón principal = lector USB (el más usado en el mostrador); las otras dos
// formas de abrir el escáner quedan en el dropdown en vez de 3 botones
// naranjos sueltos compitiendo por espacio en la topbar.
const SCANNER_OPEN_OPTIONS = [
  { key: 'usb',    label: 'Abrir lector USB', icon: FiHardDrive,  path: '#/admin/scanner-usb' },
  { key: 'camera', label: 'Abrir cámara',     icon: FiMaximize2,  path: '#/admin/scanner' },
  { key: 'manual', label: 'Abrir modo manual',icon: FiSearch,     path: '#/admin/scanner-manual' },
];

const TABS = [
  { id: 'historial',  label: 'Historial de Accesos', icon: FiList,   badge: null },
  { id: 'curso',      label: 'Por Curso',             icon: FiUsers,  badge: null },
  { id: 'estados',    label: 'Estados de Alumnos',    icon: FiUser,   badge: null },
  { id: 'salidas',    label: 'Salidas Pedagógicas',   icon: FiArrowLeft, badge: null },
  { id: 'enfermedad', label: 'Salidas por Enfermedad', icon: FiActivity, badge: null },
  { id: 'retiros',    label: 'Retiros Anticipados',    icon: FiUserMinus, badge: null },
];

// ── Punto vivo animado ────────────────────────────────────────────────────────
const LiveDot = ({ count }) => (
  <Flex align="center" gap="7px" fontSize="13px" fontWeight="700" color="#3DB871">
    <Box
      w="8px" h="8px" borderRadius="full" bg="#3DB871"
      sx={{
        animation: 'pulse 1.8s ease-in-out infinite',
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
          '50%': { opacity: 0.5, transform: 'scale(1.35)' },
        },
      }}
    />
    En vivo · {count} mov. hoy
  </Flex>
);

const AccessControl = () => {
  const [activeTab, setActiveTab] = useState('historial');
  const [todayCount, setTodayCount] = useState(null);
  const accessLogsRef = useRef(null);
  const toast = useToast();
  // Gestión de lectores (ScannerAdmin, tab "Lectores QR") queda reservada a
  // administradores — es configuración de hardware, no operación diaria.
  const canManageScanners = useRoleModuleAccess(MODULES.SISTEMA);
  // Abrir el escáner (USB/cámara/manual): administrador e inspector. Profesor
  // ve el resto de "Accesos QR" (historial, estados, salidas) pero no esto.
  // Regla centralizada en useCanOpenScanner (utils/site.js).
  const canOpenScanner = useCanOpenScanner();

  // Conteo "en vivo" de movimientos de hoy
  useEffect(() => {
    // Día calendario LOCAL, no UTC — toISOString() da el día UTC, que entre
    // ~20:00 y 23:59 hora Chile ya es el día siguiente.
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    getAccessLogs({ date: today, per_page: 1 })
      .then(res => setTodayCount(res.meta?.total ?? 0))
      .catch(() => setTodayCount(0));
  }, []);

  // Exportar CSV desde AccessLogs — se delega mediante ref o evento custom
  const handleExportCSV = () => {
    const btn = document.getElementById('access-logs-export-btn');
    if (btn) btn.click();
    else toast({ title: 'Cambia a la pestaña Historial para exportar', status: 'info', duration: 2500 });
  };

  return (
    <Box fontFamily="jakarta" minH="100vh" bg="cream.bg">

      {/* ── Topbar ────────────────────────────────────────────────────────────── */}
      <Flex
        minH={{ base: 'auto', md: '64px' }}
        px={{ base: '14px', md: '28px' }}
        py={{ base: '10px', md: 0 }}
        bg="white"
        borderBottom="1px solid" borderColor="cream.line"
        align="center" gap={{ base: '8px', md: '14px' }}
        flexWrap="wrap"
        position={{ base: 'static', md: 'sticky' }} top={{ md: '64px' }} zIndex={10}
      >
        <Box flexShrink={0}>
          <Text fontSize={{ base: '16px', md: '19px' }} fontWeight="800" color="ink.900" letterSpacing="-0.02em" lineHeight={1.2}>
            Accesos QR
          </Text>
          <Text fontSize="12.5px" color="ink.400" fontWeight="500" mt="1px" display={{ base: 'none', md: 'block' }}>
            Lectores, historial de movimientos y estados especiales
          </Text>
        </Box>

        <HStack spacing="10px" ml="auto" flexWrap="wrap" justify="flex-end">
          {todayCount !== null && <LiveDot count={todayCount} />}

          <Button
            h="40px" px={4} borderRadius="12px"
            border="1.5px solid" borderColor="cream.line2"
            bg="white" color="ink.700" fontWeight="700" fontSize="13.5px"
            _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
            leftIcon={<FiDownload size={14} />}
            display={{ base: 'none', md: 'flex' }}
            onClick={handleExportCSV}
          >
            Exportar CSV
          </Button>

          {/* Botón partido: acción principal = abrir lector USB directo;
              el chevron despliega las otras 2 formas de abrir el escáner.
              Disponible para administradores e inspector; "Lectores QR"
              (gestión de hardware, más abajo) sigue solo para administradores. */}
          {canOpenScanner && (
            <HStack spacing="1px">
              <Button
                h={{ base: '36px', md: '40px' }} px={4}
                borderLeftRadius="12px" borderRightRadius={0}
                bg="coral.500" color="#fff" fontWeight="700" fontSize="13.5px"
                _hover={{ bg: 'coral.600' }}
                boxShadow="0 8px 18px -8px rgba(239,90,42,.65)"
                leftIcon={<FiHardDrive size={14} />}
                onClick={() => window.open(SCANNER_OPEN_OPTIONS[0].path, '_blank')}
              >
                <Text display={{ base: 'none', sm: 'block' }}>{SCANNER_OPEN_OPTIONS[0].label}</Text>
              </Button>

              <Menu placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  icon={<FiChevronDown size={16} />}
                  aria-label="Otras formas de abrir el escáner"
                  title="Otras formas de abrir el escáner"
                  h={{ base: '36px', md: '40px' }} w={{ base: '32px', md: '36px' }} minW="unset"
                  borderLeftRadius={0} borderRightRadius="12px"
                  bg="coral.500" color="#fff"
                  _hover={{ bg: 'coral.600' }}
                  _active={{ bg: 'coral.600' }}
                  boxShadow="0 8px 18px -8px rgba(239,90,42,.65)"
                  borderLeft="1px solid rgba(255,255,255,.35)"
                />
                <MenuList borderRadius="12px" border="1px solid" borderColor="cream.line" boxShadow="0 12px 32px -8px rgba(35,30,26,.18)" minW="220px">
                  {SCANNER_OPEN_OPTIONS.slice(1).map(opt => (
                    <MenuItem
                      key={opt.key}
                      icon={<Icon as={opt.icon} boxSize="14px" color="coral.600" />}
                      fontSize="13.5px" fontWeight="600" color="ink.700"
                      _hover={{ bg: 'coral.tint', color: 'coral.700' }}
                      _focus={{ bg: 'coral.tint', color: 'coral.700' }}
                      onClick={() => window.open(opt.path, '_blank')}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                  {/* A diferencia de las opciones de arriba (abren el escáner
                      en una pestaña nueva), esta es la vista de administración
                      de lectores (ScannerAdmin) — cambia de tab en la misma
                      página en vez de abrir una URL. Solo administradores. */}
                  {canManageScanners && (
                    <>
                      <MenuDivider />
                      <MenuItem
                        icon={<Icon as={FiGrid} boxSize="14px" color="coral.600" />}
                        fontSize="13.5px" fontWeight="600" color="ink.700"
                        _hover={{ bg: 'coral.tint', color: 'coral.700' }}
                        _focus={{ bg: 'coral.tint', color: 'coral.700' }}
                        onClick={() => setActiveTab('lectores')}
                      >
                        Lectores QR
                      </MenuItem>
                    </>
                  )}
                </MenuList>
              </Menu>
            </HStack>
          )}
        </HStack>
      </Flex>

      {/* ── Tabs ──────────────────────────────────────────────────────────────── */}
      {/* Pastillas coral/cream (mismo lenguaje que el resto del rediseño de
          Accesos QR) en vez de subrayado angosto — más aire vertical y
          objetivos de toque más grandes, en línea con las proporciones del
          resto de la UI. */}
      <Box
        bg="white" borderBottom="1px solid" borderColor="cream.line"
        px={{ base: '14px', md: '28px' }} py={{ base: '12px', md: '16px' }}
        overflowX="auto"
        sx={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
      >
        <HStack spacing={{ base: '8px', md: '10px' }}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <Button
                key={tab.id}
                variant="unstyled"
                display="flex" alignItems="center" gap="8px"
                px={{ base: '16px', md: '20px' }} h={{ base: '38px', md: '42px' }}
                fontSize="13.5px" fontWeight="700"
                borderRadius="12px"
                bg={isActive ? 'coral.500' : 'cream.bg2'}
                color={isActive ? '#fff' : 'ink.700'}
                boxShadow={isActive ? '0 6px 16px -6px rgba(239,90,42,.55)' : 'none'}
                whiteSpace="nowrap"
                flexShrink={0}
                _hover={{ bg: isActive ? 'coral.600' : 'coral.soft', color: isActive ? '#fff' : 'coral.700' }}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon as={tab.icon} boxSize="16px" />
                {tab.label}
                {tab.badge && (
                  <Flex
                    as="span"
                    px="5px" h="18px" minW="18px"
                    borderRadius="999px" align="center" justify="center"
                    bg={isActive ? 'whiteAlpha.300' : 'coral.soft'} color={isActive ? '#fff' : 'coral.700'}
                    fontSize="11px" fontWeight="800"
                  >
                    {tab.badge}
                  </Flex>
                )}
              </Button>
            );
          })}
        </HStack>
      </Box>

      {/* ── Contenido del tab ─────────────────────────────────────────────────── */}
      <Box px={{ base: 3, md: '28px' }} py={{ base: 4, md: '22px' }} pb={{ base: '80px', md: '40px' }}>
        {activeTab === 'historial' && <AccessLogs />}
        {activeTab === 'curso'     && <AttendanceView />}
        {activeTab === 'lectores'  && canManageScanners && <ScannerAdmin />}
        {activeTab === 'estados'   && <StudentStatusesTab />}
        {activeTab === 'salidas'   && <PedagogicalExitsTab />}
        {activeTab === 'enfermedad' && <SickExitsTab />}
        {activeTab === 'retiros'    && <PickupExitsTab />}
      </Box>
    </Box>
  );
};

export default AccessControl;
